import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { PostPlatform } from "../../../../shared/enums/campaignPost.enums";
import { BrandCampaignFacebookPost } from "../../../../shared/models/brandCampaignFacebookPost";
import { BrandCampaignGoogleAdPost } from "../../../../shared/models/brandCampaignGoogleAdPost";
import { BrandCampaignLinkedInPost } from "../../../../shared/models/brandCampaignLinkedInPost";
import {
  BrandCampaignPost,
  BrandPost,
} from "../../../../shared/models/brandCampaignPost";
import { Image } from "../../../../shared/models/image";
import { BrandService } from "../../../../shared/services/api/brand.service";
import { HttpClient } from "../../../../shared/services/http-client";
import { WebLinkMetadataResponse } from "../../../../shared/services/responses/web-link-metadata-response";
import { UnreachableCaseError } from "../../../../../harmony/core";

export interface BrandCampaignPostReview {
  reviewImageComment: string;
  isReviewImageCommentResolved: boolean;
  reviewTextComment: string;
  isReviewTextCommentResolved: boolean;
  isReviewApproved: boolean;
}

@Injectable({ providedIn: "root" })
export class BrandCampaignPostService {
  constructor(
    private http: HttpClient,
    private brandService: BrandService,
  ) {}

  public createBrandCampaignGooglePost(
    campaignId: number,
    detailsId: number,
    categoryId: number,
    post: BrandCampaignGoogleAdPost,
  ): Observable<BrandCampaignGoogleAdPost> {
    const brandId = this.brandService.currentBrandId;
    return this.http
      .post(
        `brand/${brandId}/campaign/${campaignId}/detail/${detailsId}/category/${categoryId}/google_post`,
        post,
      )
      .pipe(map(({ body }) => new BrandCampaignGoogleAdPost(body)));
  }

  public updateBrandCampaignGooglePost(
    campaignId: number,
    detailsId: number,
    categoryId: number,
    post: BrandCampaignGoogleAdPost,
  ): Observable<BrandCampaignGoogleAdPost> {
    const brandId = this.brandService.currentBrandId;
    return this.http
      .put(
        `brand/${brandId}/campaign/${campaignId}/detail/${detailsId}/category/${categoryId}/google_post/${post.id}`,
        post,
      )
      .pipe(map(({ body }) => new BrandCampaignGoogleAdPost(body)));
  }

  public createBrandCampaignLinkedInPost(
    campaignId: number,
    detailsId: number,
    categoryId: number,
    post: BrandCampaignLinkedInPost,
  ): Observable<BrandCampaignLinkedInPost> {
    const brandId = this.brandService.currentBrandId;
    return this.http
      .post(
        `brand/${brandId}/campaign/${campaignId}/detail/${detailsId}/category/${categoryId}/linkedin_post`,
        post,
      )
      .pipe(map(({ body }) => new BrandCampaignLinkedInPost(body)));
  }

  public updateBrandCampaignLinkedInPost(
    campaignId: number,
    detailsId: number,
    categoryId: number,
    post: BrandCampaignLinkedInPost,
  ): Observable<BrandCampaignLinkedInPost> {
    const brandId = this.brandService.currentBrandId;
    return this.http
      .put(
        `brand/${brandId}/campaign/${campaignId}/detail/${detailsId}/category/${categoryId}/linkedin_post/${post.id}`,
        post,
      )
      .pipe(map(({ body }) => new BrandCampaignLinkedInPost(body)));
  }

  getCreateCampaignDetailCategoryGooglePostUrl(
    campaignId: number,
    detailsId: number,
    categoryId: number,
  ): string {
    const brandId = this.brandService.currentBrandId;
    return this.http.createRequestUrlV1(
      `brand/${brandId}/campaign/${campaignId}/detail/${detailsId}/category/${categoryId}/google_post`,
    );
  }

  getAddCampaignDetailCategoryPostFileUrl(
    campaignId: number,
    detailsId: number,
    categoryId: number,
  ): string {
    const brandId = this.brandService.currentBrandId;
    return this.http.createRequestUrlV1(
      `brand/${brandId}/campaign/${campaignId}/detail/${detailsId}/category/${categoryId}/post_file`,
    );
  }

  deleteCampaignDetailCategoryPostFile(
    campaignId: number,
    detailId: number,
    categoryId: number,
    fileId: number,
  ): Observable<void> {
    const brandId = this.brandService.currentBrandId;
    return this.http
      .del(
        `brand/${brandId}/campaign/${campaignId}/detail/${detailId}/category/${categoryId}/post_file/${fileId}`,
      )
      .pipe(map(() => {}));
  }

  deleteCampaignDetailCategoryPost(
    campaignId: number,
    detailId: number,
    categoryId: number,
    postId: number,
  ): Observable<void> {
    const brandId = this.brandService.currentBrandId;
    return this.http
      .del(
        `brand/${brandId}/campaign/${campaignId}/detail/${detailId}/category/${categoryId}/post/${postId}`,
      )
      .pipe(map(() => {}));
  }

  getCreateCampaignDetailCategoryPostUrl(
    campaignId: number,
    detailsId: number,
    categoryId: number,
  ): string {
    const brandId = this.brandService.currentBrandId;
    return this.http.createRequestUrlV1(
      `brand/${brandId}/campaign/${campaignId}/detail/${detailsId}/category/${categoryId}/post`,
    );
  }

  createCampaignDetailCategoryPost(
    campaignId: number,
    detailsId: number,
    categoryId: number,
    post: BrandCampaignFacebookPost,
  ): Observable<BrandCampaignFacebookPost> {
    const brandId = this.brandService.currentBrandId;
    return this.http
      .post(
        `brand/${brandId}/campaign/${campaignId}/detail/${detailsId}/category/${categoryId}/post`,
        JSON.stringify(post),
      )
      .pipe(map(({ body }) => new BrandCampaignFacebookPost(body)));
  }

  updateCampaignDetailCategoryPost(
    campaignId: number,
    detailsId: number,
    categoryId: number,
    post: BrandCampaignFacebookPost,
  ): Observable<BrandCampaignFacebookPost> {
    const brandId = this.brandService.currentBrandId;
    return this.http
      .post(
        `brand/${brandId}/campaign/${campaignId}/detail/${detailsId}/category/${categoryId}/post/${post.id}`,
        JSON.stringify(post),
      )
      .pipe(map(({ body }) => new BrandCampaignFacebookPost(body)));
  }

  updateCampaignDetailCategoryPostReview(
    campaignId: number,
    detailsId: number,
    categoryId: number,
    postId: number,
    data: BrandCampaignPostReview,
  ): Observable<BrandPost> {
    const brandId = this.brandService.currentBrandId;
    return this.http
      .put(
        `brand/${brandId}/campaign/${campaignId}/detail/${detailsId}/category/${categoryId}/post/${postId}/review`,
        JSON.stringify(data),
      )
      .pipe(
        map(({ body }) => {
          const platform: PostPlatform = body.platform;

          switch (platform) {
            case PostPlatform.Facebook:
              return new BrandCampaignFacebookPost(body);
            case PostPlatform.Google:
              return new BrandCampaignGoogleAdPost(body);
            case PostPlatform.LinkedIn:
              return new BrandCampaignLinkedInPost(body);
            default:
              throw new UnreachableCaseError(platform);
          }
        }),
      );
  }

  updateCampaignPostCategory(
    campaignId: number,
    detailsId: number,
    categoryId: number,
    postId: number,
    newCategoryId: number,
  ): Observable<BrandCampaignPost> {
    const brandId = this.brandService.currentBrandId;
    return this.http
      .put(
        `brand/${brandId}/campaign/${campaignId}/detail/${detailsId}/category/${categoryId}/post/${postId}/category`,
        JSON.stringify({ newCategoryId: newCategoryId }),
      )
      .pipe(
        map(({ body }) => {
          const platform: PostPlatform = body.platform;

          switch (platform) {
            case PostPlatform.Facebook:
              return new BrandCampaignFacebookPost(body);
            case PostPlatform.Google:
              return new BrandCampaignGoogleAdPost(body);
            case PostPlatform.LinkedIn:
              return new BrandCampaignLinkedInPost(body);
            default:
              throw new UnreachableCaseError(platform);
          }
        }),
      );
  }

  getLinkMetadata(webLink: string): Observable<WebLinkMetadataResponse> {
    return this.http
      .post(`web_link_metadata`, { link: webLink })
      .pipe(
        map(
          ({ body }) =>
            new WebLinkMetadataResponse(body.imageUrl, body.description),
        ),
      );
  }

  createPostImageFromUrl(
    campaignId: number,
    detailsId: number,
    categoryId: number,
    imageUrl: string,
  ): Observable<Image> {
    const brandId = this.brandService.currentBrandId;
    return this.http
      .post(
        `brand/${brandId}/campaign/${campaignId}/detail/${detailsId}/category/${categoryId}/post_url_file`,
        { imageUrl: imageUrl },
      )
      .pipe(map(({ body }) => new Image(body)));
  }
}
